import { useEffect, useState, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Sparkles, Zap, Crown, Rocket, Loader2, Users, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTier } from '@/components/EnterpriseGate';
import { checkoutAPI } from '@/lib/api';
import { track, EVENTS } from '@/lib/telemetry';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PLANS = [
  {
    id: 'basic', name: 'Basic', price: 'Free', period: '',
    blurb: 'Explore the platform with demo data.',
    icon: Sparkles, accent: 'text-muted-foreground',
    features: ['Dashboard + demo data', 'Products & suppliers preview', 'PCF viewer (read-only)', 'TÜV docs preview', 'Community support'],
  },
  {
    id: 'starter', name: 'CBAM Starter', price: '€490', period: '/ year',
    blurb: 'Everything you need to file CBAM & ICS2 declarations.',
    icon: Rocket, accent: 'text-emerald-500', highlight: true, stripe: 'starter_annual',
    features: ['All Basic features', 'Up to 3 API keys', 'Unlimited ICS2 ENS shipments',
      'Link shipments ↔ PCF reports', 'TÜV docs pack (Markdown preview)', 'Email support'],
  },
  {
    id: 'pro', name: 'Pro', price: '€1,990', period: '/ year',
    blurb: 'Collaborate across CFO, CPO and CSO with live supplier data.',
    icon: Users, accent: 'text-blue-500',
    features: ['All Starter features', 'Role dashboards (CFO · CPO · CSO)', 'Supplier portal (invitations + tracking)',
      'Unlimited API keys (read + write)', 'CSV imports', 'Priority email support'],
  },
  {
    id: 'advanced', name: 'Advanced', price: '€6,500', period: '/ year',
    blurb: 'For growing sustainability teams that need live intelligence.',
    icon: Zap, accent: 'text-indigo-500',
    features: ['All Pro features', 'Living PCF (continuous monitoring)', 'Mapping profiles & automation',
      'Advanced analytics & scenario modelling', 'Custom audit workflows', 'Dedicated onboarding'],
  },
  {
    id: 'enterprise', name: 'Enterprise', price: 'from €12,000', period: '/ year',
    blurb: 'Audit-grade deployments with SAP / DATEV.',
    icon: Crown, accent: 'text-amber-500',
    features: ['All Advanced features', 'SAP S/4HANA integration', 'DATEV integration',
      'Admin TÜV pack ZIP download', 'Dedicated success manager', 'SLA & onboarding'],
  },
];

export default function Pricing() {
  const { tier: currentTier, setTier } = useTier();
  const [switching, setSwitching] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const polledRef = useRef(false);

  // Fallback plan switch (basic / advanced) — flips the app-wide tier singleton.
  const choosePlan = async (planId) => {
    if (planId === currentTier) return;
    setSwitching(planId);
    try {
      await axios.post(`${API}/user/tier`, { tier: planId });
      setTier(planId);
      toast.success(`Plan set to ${planId}`);
    } catch {
      toast.error('Failed to update plan');
    } finally {
      setSwitching(null);
    }
  };

  // Lemon Squeezy checkout — replaces Stripe at go-live. Works for starter /
  // pro / advanced / enterprise; each tier maps to a different LS variant id
  // on the backend (configured via env vars). Returns a hosted checkout URL.
  const subscribeViaLemonSqueezy = async (tier) => {
    setSwitching(tier);
    track(EVENTS.CHECKOUT_STARTED, { plan: `${tier}_annual`, provider: 'lemon_squeezy' });
    try {
      const res = await checkoutAPI.createLemonSqueezySession(tier);
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error('Lemon Squeezy did not return a checkout URL');
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 503) {
        toast.error(detail || 'Checkout for this plan is not yet available. Please contact sales.');
      } else {
        toast.error(detail || 'Checkout failed. Please try again.');
      }
    } finally {
      setSwitching(null);
    }
  };

  // Handle the return-from-Stripe polling exactly once per session_id
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const cancelled = params.get('stripe_cancelled');
    if (cancelled) {
      toast.info('Checkout cancelled — you can try again anytime.');
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    if (!sessionId || polledRef.current) return;
    polledRef.current = true;

    let attempts = 0;
    const maxAttempts = 8;
    const interval = 2500;
    setPaymentStatus({ state: 'pending', message: 'Verifying your payment…' });

    const poll = async () => {
      attempts += 1;
      try {
        const r = await checkoutAPI.status(sessionId);
        const { payment_status: ps, status, tier } = r.data;
        if (ps === 'paid' || status === 'paid') {
          setPaymentStatus({ state: 'success', message: 'Welcome to CBAM Starter!' });
          if (tier) setTier(tier);
          track(EVENTS.SUBSCRIPTION_ACTIVATED, { tier: tier || 'starter' });
          toast.success('Payment successful — CBAM Starter unlocked');
          window.history.replaceState({}, '', window.location.pathname);
          return;
        }
        if (status === 'expired' || status === 'cancelled') {
          setPaymentStatus({ state: 'error', message: 'Session expired or cancelled.' });
          return;
        }
        if (attempts >= maxAttempts) {
          setPaymentStatus({ state: 'pending', message: 'Still processing — check back in a few minutes.' });
          return;
        }
        setPaymentStatus({ state: 'pending', message: `Verifying your payment… (attempt ${attempts}/${maxAttempts})` });
        setTimeout(poll, interval);
      } catch (err) {
        // Transient errors (502 / network) — keep polling while attempts remain
        if (attempts < maxAttempts) {
          setPaymentStatus({ state: 'pending', message: `Verifying your payment… (attempt ${attempts}/${maxAttempts})` });
          setTimeout(poll, interval);
          return;
        }
        setPaymentStatus({ state: 'error', message: 'Could not verify payment. Please refresh in a minute.' });
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-6 space-y-8" data-testid="pricing-page">
      <div className="text-center max-w-3xl mx-auto">
        <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">Choose Your Compliance Readiness Level</h1>
        <p
          className="mt-4 text-lg sm:text-xl font-semibold text-primary"
          data-testid="pricing-value-prop"
        >
          Replace €2,000–€10,000 consultant reports with a €29 audit-ready PDF.
        </p>
        <p className="text-sm sm:text-base text-muted-foreground mt-3">
          Reduce regulatory risk at a fraction of consulting costs. Every plan includes
          methodology transparency, audit trail and human-review workflow — so you can
          comply with confidence.
        </p>
        <div
          className="mt-4 inline-flex items-start gap-2 rounded-lg border border-amber-400/50 bg-amber-50 dark:bg-amber-950/30 px-4 py-2 text-sm text-amber-900 dark:text-amber-200"
          data-testid="pricing-cbam-urgency"
        >
          <Zap className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            <strong>Avoid CBAM penalties and export delays.</strong> Start reporting now — every quarter of inaction adds to your first-declaration risk.
          </span>
        </div>
      </div>

      {paymentStatus && (
        <Card className={`max-w-3xl mx-auto p-4 text-sm flex items-center gap-3 ${
          paymentStatus.state === 'success' ? 'border-emerald-500/40 bg-emerald-500/5'
            : paymentStatus.state === 'error' ? 'border-destructive/40 bg-destructive/5'
            : 'border-amber-500/40 bg-amber-500/5'
        }`} data-testid="payment-status">
          {paymentStatus.state === 'pending' && <Loader2 className="w-4 h-4 animate-spin" />}
          {paymentStatus.state === 'success' && <Check className="w-4 h-4 text-emerald-500" />}
          <span>{paymentStatus.message}</span>
        </Card>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 max-w-7xl mx-auto">
        {PLANS.map((plan) => {
          const Icon = plan.icon;
          const isCurrent = plan.id === currentTier;
          return (
            <Card
              key={plan.id}
              data-testid={`plan-${plan.id}`}
              className={`p-6 flex flex-col ${plan.highlight ? 'ring-2 ring-primary shadow-lg relative' : ''}`}
            >
              {plan.highlight && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2" data-testid="plan-starter-badge">
                  Most popular
                </Badge>
              )}
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-5 h-5 ${plan.accent}`} />
                <h3 className="font-semibold">{plan.name}</h3>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-bold" data-testid={`plan-${plan.id}-price`}>{plan.price}</span>
                {plan.period && <span className="text-sm text-muted-foreground">{plan.period}</span>}
              </div>
              <p className="text-xs text-muted-foreground mb-5">{plan.blurb}</p>

              <ul className="space-y-2 text-sm flex-1 mb-5">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    <span className="text-muted-foreground">{f}</span>
                  </li>
                ))}
              </ul>

              {isCurrent ? (
                <Button variant="outline" disabled data-testid={`plan-${plan.id}-current`}>Current plan</Button>
              ) : plan.id === 'enterprise' ? (
                <Link to="/pilot-consultation">
                  <Button className="w-full" variant="outline" data-testid={`plan-${plan.id}-contact`}>Request Compliance Assessment</Button>
                </Link>
              ) : plan.id === 'basic' ? (
                <Button
                  onClick={() => choosePlan(plan.id)}
                  disabled={switching === plan.id}
                  variant="outline"
                  data-testid={`plan-${plan.id}-choose`}
                  className="w-full"
                >
                  {switching === plan.id ? 'Switching…' : 'Start with Basic'}
                </Button>
              ) : plan.id === 'starter' ? (
                <div className="grid grid-cols-1 gap-2">
                  <Link to="/pilot-consultation">
                    <Button className="w-full" data-testid={`plan-${plan.id}-start-pilot`}>Start Pilot</Button>
                  </Link>
                  <Button
                    onClick={() => subscribeViaLemonSqueezy('starter')}
                    disabled={switching === 'starter'}
                    variant="outline"
                    className="w-full"
                    data-testid={`plan-${plan.id}-subscribe`}
                  >
                    {switching === 'starter' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Subscribe {plan.price} / year
                  </Button>
                </div>
              ) : (plan.id === 'pro' || plan.id === 'advanced') ? (
                <div className="grid grid-cols-1 gap-2">
                  <Button
                    onClick={() => subscribeViaLemonSqueezy(plan.id)}
                    disabled={switching === plan.id}
                    className="w-full"
                    data-testid={`plan-${plan.id}-subscribe`}
                  >
                    {switching === plan.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Subscribe {plan.price} / year
                  </Button>
                  <Link to="/pilot-consultation">
                    <Button className="w-full" variant="outline" data-testid={`plan-${plan.id}-start-pilot`}>Start Pilot</Button>
                  </Link>
                </div>
              ) : (
                <Link to="/pilot-consultation">
                  <Button className="w-full" data-testid={`plan-${plan.id}-start-pilot`}>Start Pilot</Button>
                </Link>
              )}

              <a
                href="/methodology.html"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 text-xs text-center text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-primary"
                data-testid={`plan-${plan.id}-sample-report`}
              >
                View sample report
              </a>
            </Card>
          );
        })}
      </div>

      <Card
        className="max-w-3xl mx-auto p-5 flex flex-col sm:flex-row sm:items-center gap-4 border-emerald-500/30 bg-emerald-500/5"
        data-testid="pay-per-report-card"
      >
        <div className="flex-1">
          <div className="text-sm font-semibold text-emerald-700 dark:text-emerald-300 mb-1">
            Pay-per-Report · €29 per audit-ready PDF
          </div>
          <p className="text-xs text-muted-foreground">
            Ideal for first-time users. No subscription commitment — buy one ISO 14067 report,
            keep the audit chain, and upgrade to an annual plan whenever you&apos;re ready.
          </p>
        </div>
        <Link to="/pilot-request" data-testid="pay-per-report-cta">
          <Button size="sm" variant="outline" className="border-emerald-500/50 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/10">
            Buy a single report
          </Button>
        </Link>
      </Card>

      <Card
        className="max-w-5xl mx-auto p-8 bg-gradient-to-br from-primary/5 to-accent/5 border-primary/30"
        data-testid="pilot-cta-card"
      >
        <div className="flex flex-col md:flex-row md:items-start gap-6 justify-between">
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-3">
              <Rocket className="w-3.5 h-3.5" /> CBAM Readiness Pilot
            </div>
            <h2 className="text-2xl font-heading font-bold tracking-tight mb-2">
              Not sure where to start? Book your CBAM Readiness Pilot.
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              A fixed‑scope <strong>60–90 day</strong> engagement that moves you from
              scattered supplier data to an audit‑ready CBAM quarterly report, with a
              live dry‑run reviewed by a Veridex compliance specialist.
            </p>
            <ul className="grid sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
              {[
                'Guided onboarding + data-source inventory',
                'PCF calculation for your first SKUs',
                'Human review of every report before filing',
                'Audit-ready deliverables hand-off to your auditor',
              ].map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /> {b}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-muted-foreground">
              Priced from <strong>€3,000</strong>. Credit applied to your annual plan upon conversion.
            </p>
          </div>
          <Link to="/pilot-consultation" data-testid="book-pilot-cta">
            <Button size="lg" className="bg-primary hover:bg-primary/90 whitespace-nowrap">
              Book Your Pilot <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </Card>

      <Card className="max-w-4xl mx-auto p-5 text-xs text-muted-foreground" data-testid="pricing-note">
        Prices are indicative for early-access customers. Plan switches take effect
        immediately for feature gating. Annual subscriptions are processed by
        <strong> Lemon Squeezy</strong> (Merchant of Record — handles EU VAT and invoicing).
      </Card>
    </div>
  );
}
